import { useState } from 'react';
import { Row, Col } from 'reactstrap';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import moment from 'moment';
import Avatar from 'src/componentes/avatar';
import Button from 'src/componentes/button';
import Mentions from 'src/componentes/mentions';
import CurtirComentarioButton from './CurtirComentario';
import useAuth from 'src/hooks/useAuth';
import http from 'src/services/httpService';
import errorHandler from 'src/utils/error-handler';
import LucideIcon from 'src/componentes/lucide-icon';
import { isEmptyComment, sanitizeCommentHtml } from './utils';

const ComentarioItem = ({
  comentario,
  nivel = 0,
  onEdit,
  onDelete,
  onUpdateRespostas,
  onIncrementQuantidade,
  endpoints,
  replyEditor: ReplyEditor = Mentions,
}) => {
  const intl = useIntl();
  const { user } = useAuth();
  const resources = useSelector((state) => state.user.termos);

  const [respondendo, setRespondendo] = useState(false);
  const [textoResposta, setTextoResposta] = useState('');
  const [respostasAbertas, setRespostasAbertas] = useState(false);
  const [respostas, setRespostas] = useState([]);
  const [isLoadingRespostas, setIsLoadingRespostas] = useState(false);

  const carregarRespostas = async () => {
    if (respostasAbertas) {
      setRespostasAbertas(false);
      return;
    }

    try {
      setIsLoadingRespostas(true);
      const response = await http.post(endpoints.obterRespostas, {
        comentarioPaiId: comentario.id,
      });
      setRespostas(response.data || []);
      setRespostasAbertas(true);
    } catch (err) {
      errorHandler(err);
    } finally {
      setIsLoadingRespostas(false);
    }
  };

  const enviarResposta = async () => {
    if (isEmptyComment(textoResposta)) return;

    try {
      await http.post(endpoints.responder, {
        comentarioPaiId: comentario.id,
        descricao: sanitizeCommentHtml(textoResposta),
      });

      setTextoResposta('');
      setRespondendo(false);

      const response = await http.post(endpoints.obterRespostas, {
        comentarioPaiId: comentario.id,
      });

      //evita problema no map
      setRespostas(response.data || []);

      setRespostasAbertas(true);

      // atualizar contador
      if (onUpdateRespostas) {
        onUpdateRespostas();
      }
      if (onIncrementQuantidade) {
        onIncrementQuantidade(1);
      }
    } catch (err) {
      errorHandler(err);
    }
  };

  // controla visual dos comentarios aninhados, descolando pra direita e definindo tamanho do avatar
  const indentacao = nivel * 1.25;
  const avatarSize = nivel === 0 ? 40 : 32;

  return (
    <div
      style={{
        marginLeft: `${indentacao}rem`,
        marginBottom: nivel === 0 ? '1.25rem' : '0',
      }}
    >
      <Row>
        {/* coluna do avatar */}
        <Col xs="auto" style={{ paddingRight: '0.5rem' }}>
          <Avatar
            foto={comentario.fotoColaborador}
            size={avatarSize}
            label={comentario.nomeColaborador}
            showLabel={false}
          />
        </Col>

        {/* coluna do conteudo */}
        <Col style={{ paddingLeft: '0' }}>
          {/* Nome e Data */}
          <div className="mb-2">
            <div className="font-weight-bolder">
              {comentario.nomeColaborador}
            </div>
            <div className="text-muted small">
              {intl.formatMessage(
                { id: 'emDataAsHoras' },
                {
                  data: moment(comentario.data).locale(intl.locale).format('L'),
                  horas: moment(comentario.data).format('HH:mm'),
                }
              )}
            </div>
          </div>

          {/* texto do comentario */}
          <div className="mb-2">
            <Mentions
              value={sanitizeCommentHtml(comentario.descricao)}
              disabled={true}
              currentUserId={user?.id}
              style={{
                border: 'none',
                padding: '0',
                cursor: 'default',
                minHeight: '2.5rem',
                fontFamily: 'inherit',
                fontWeight: 600,
                fontSize: '0.875rem',
                lineHeight: '1.25rem',
                letterSpacing: '0',
                color: 'rgba(163, 163, 163, 1)',
              }}
            />
          </div>

          {/* Curtir, Comentar, Editar, Excluir */}
          <div className="d-flex align-items-center">
            <CurtirComentarioButton
              curtidas={comentario.curtidas}
              comentarioId={comentario.id}
              endpoint={endpoints.curtir}
            />

            {/* Btn comentar apenas se nao for uma resposta já */}
            {nivel === 0 && (
              <>
                <div
                  className="cursor-pointer d-flex align-items-center text-primary font-weight-bolder mr-4"
                  onClick={() => setRespondendo(!respondendo)}
                  style={{ fontSize: '0.875rem' }}
                >
                  {/* icone de comentario */}
                  <LucideIcon icon="MessageCircle" size={18} className="mr-1" />
                  {intl.formatMessage({ id: 'labelComentar' })}
                </div>

                {/* Botão para ver respostas */}
                {comentario.quantidadeRespostas > 0 && (
                  <div
                    className="cursor-pointer text-primary font-weight-bolder mr-4"
                    onClick={carregarRespostas}
                    style={{ fontSize: '0.875rem' }}
                  >
                    {isLoadingRespostas
                      ? intl.formatMessage({ id: 'carregando' })
                      : respostasAbertas
                      ? comentario.quantidadeRespostas === 1
                        ? intl.formatMessage({ id: 'umComentarioExibido' })
                        : intl.formatMessage(
                            { id: 'nComentariosExibidos' },
                            { numero: comentario.quantidadeRespostas }
                          )
                      : comentario.quantidadeRespostas === 1
                      ? intl.formatMessage({ id: 'exibirUmComentario' })
                      : intl.formatMessage(
                          { id: 'exibirNComentarios' },
                          { numero: comentario.quantidadeRespostas }
                        )}
                  </div>
                )}
              </>
            )}

            {comentario.podeEditar && onEdit && onDelete && (
              <>
                <div
                  className="cursor-pointer text-primary font-weight-bolder mr-3"
                  onClick={() => onEdit(comentario)}
                  style={{ fontSize: '0.875rem' }}
                >
                  {intl.formatMessage(
                    { id: 'label.editar' },
                    { editar: resources.editar }
                  )}
                </div>
                <div
                  className="cursor-pointer text-primary font-weight-bolder"
                  onClick={() => onDelete(comentario.id)}
                  style={{ fontSize: '0.875rem' }}
                >
                  {intl.formatMessage({ id: 'label.excluir' })}
                </div>
              </>
            )}
          </div>

          {/* Input para responder */}
          {respondendo && (
            <div className="mt-3">
              <div className="form-group">
                <ReplyEditor
                  value={textoResposta}
                  onChange={(v) => setTextoResposta(v)}
                  placeholder={intl.formatMessage({ id: 'envieUmComentario' })}
                  style={{ minHeight: '3.75rem' }}
                />
              </div>
              <div className="d-flex justify-content-end">
                <Button
                  color="primary"
                  size="sm"
                  onClick={enviarResposta}
                  disabled={isEmptyComment(textoResposta)}
                >
                  {intl.formatMessage({ id: 'label.enviar' })}
                </Button>
                <Button
                  color="secondary"
                  size="sm"
                  className="ml-2"
                  onClick={() => {
                    setRespondendo(false);
                    setTextoResposta('');
                  }}
                >
                  {intl.formatMessage({ id: 'label.cancelar' })}
                </Button>
              </div>
            </div>
          )}

          {/* Respostas aninhadas recursirva */}
          {respostasAbertas && respostas.length > 0 && (
            <div className="mt-3">
              {respostas.map((resposta) => (
                <ComentarioItem
                  key={resposta.id}
                  comentario={resposta}
                  nivel={nivel + 1}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onUpdateRespostas={carregarRespostas}
                  onIncrementQuantidade={onIncrementQuantidade}
                  endpoints={endpoints}
                  replyEditor={ReplyEditor}
                />
              ))}
            </div>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default ComentarioItem;
